import { Close, Height } from '@mui/icons-material'
import { Box, Button, Divider, Grid, InputLabel, TextField, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import styles from '../../../../../assets/styles/componentStyles/feedbackDialog.module.scss'
import globalStyles from "../../../../../assets/styles/global.module.scss";
import Required from '../../../../../components/utility/required';
import { chatbotFeedbackOptionData, chatBotSelectedFeedbackOptionData, feedbackLevel, feedbackProvidedType } from '../../../../../models/axiosModel';
import agent from '../../../../../services/api';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import { useGenericStore } from '../../../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import { useSearchParams } from 'react-router-dom';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';

interface FeedbackDialogProps {
    // Store props - now imported from inboxStore
    // currentActiveChatbotThread: currentActiveChatbotThreadTypeOld, 
    // setCurrentActiveChatbotThread: React.Dispatch<React.SetStateAction<currentActiveChatbotThreadTypeOld>>, 
    // feedbackProvidedTypeData: feedbackProvidedType[], 
    // feedbackLevel: feedbackLevel[], 
    // chatbotThreadId: string, 
    // flagDebug: boolean,
    // Function props
    onCloseOfFeedbackForm: () => void, 
    feedbackFor: string, 
} 

const FeedbackDialog: React.FC<FeedbackDialogProps> = (props) => {

    const { currentActiveChatbotThread, setCurrentActiveChatbotThread, feedbackProvidedTypeData, feedbackLevelData, selectedMessageChatbotData } = useRuleBasedInboxStore(useShallow((state) => ({
        currentActiveChatbotThread: state.currentActiveChatbotThread,
        setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread,
        feedbackProvidedTypeData: state.feedbackProvidedTypeData,
        feedbackLevelData: state.feedbackLevelData,
        selectedMessageChatbotData: state.selectedMessageChatbotData,
    })))

    // Destructure function props
    const { onCloseOfFeedbackForm, feedbackFor } = props;
    
    // Use feedbackLevelData as feedbackLevel for backward compatibility
    const feedbackLevel = feedbackLevelData;
    // const chatbotThreadId = threadId;
    const searchParams = new URLSearchParams(window.location.search);
    const chatbotThreadId = searchParams.get('thread_id');

    const [feedbackOptionsData, setFeedbackOptionsData] = useState<chatbotFeedbackOptionData[] | null>(null);

    const [selectedIssue, setSelectedIssue] = useState<chatBotSelectedFeedbackOptionData | null>(null);
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    useEffect(() => {
        if (feedbackOptionsData === null) {
            getListOfFeedbackOptionAPiCall();
        }
    }, [])


    const getListOfFeedbackOptionAPiCall = async () => {
        if(!selectedMessageChatbotData || !selectedMessageChatbotData?.chatbot_id || !selectedMessageChatbotData?.chatbot_token) return;

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const chatbot_id = selectedMessageChatbotData?.chatbot_id;
        const chatbot_token = selectedMessageChatbotData?.chatbot_token;
        const response = await agent.getListOfFeedbackOption.get(String(chatbot_id), chatbot_token);
        if (response.status) {
            if (response.data) {
                setFeedbackOptionsData(response.data.chatbot_feedback_option_data);
            }
        } else {
            // console.log("Error Occured while getting list of feedback options"); 
            handleError(response);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    const handleOnChangeOfFeedbackOptions = (value: string, feedback_option_field_id: number) => {
        setSelectedIssue((prev: chatBotSelectedFeedbackOptionData | null) => {
            if (!prev) return null;
            const updatedValue = prev?.feedback_option_field.map((field) =>
                field.feedback_option_field_id === feedback_option_field_id ? { ...field, feedback_value: value } : field
            );
            return { ...prev, feedback_option_field: updatedValue };
        })
    }

    const checkIsSubmitBtnIsDisabled = (selectedIssue: chatBotSelectedFeedbackOptionData | null) => {
        if (selectedIssue) {
            return !(selectedIssue.feedback_option_field.every((field) => {
                if (field.flag_required) {
                    return field.feedback_value.trim() != "";
                }
                return true;
            }));
        } else {
            return true;
        }
    }

    const getUniqueId = () => {
        switch (String(feedbackFor).toLowerCase()) {
            case "chronox":
                return null;
            case "chatbot":
                const chatbot_id = selectedMessageChatbotData ? selectedMessageChatbotData?.chatbot_id : undefined;
                return Number(chatbot_id);
            case "thread":
                return Number(currentActiveChatbotThread?.chatbot_user_id);
            case "message":
                return chatbotThreadId;
        }
    }

    const getProvidedTypeId = () => {
        return feedbackProvidedTypeData?.find((data: feedbackProvidedType) => data.feedback_provided_type_name === "Admin")?.feedback_provided_type_id || 0
    }

    const addFeedbackPayload = () => {
        const chatbot_id = selectedMessageChatbotData ? selectedMessageChatbotData?.chatbot_id : undefined;
        return {
            chatbot_id: Number(chatbot_id),
            feedback_option_id: selectedIssue?.feedback_option_id,
            feedback_level_id: feedbackLevel.find((item: feedbackLevel) => (item.feedback_level_name).toLowerCase() === String(feedbackFor).toLowerCase())?.feedback_level_id || 0,
            feedback_unique_id: getUniqueId() ?? '',
            feedback_provided_type_id: getProvidedTypeId(),
            feedback_option_field_data: selectedIssue?.feedback_option_field?.map((field) => {
                return {
                    feedback_option_field_id: field.feedback_option_field_id,
                    feedback_value: field.feedback_value,
                }
            })
        }
    }

    const handleSubmitClick = async () => {
        if(!selectedMessageChatbotData || !currentActiveChatbotThread) return;
        
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const chatbot_token = selectedMessageChatbotData?.chatbot_token;
        const payload = addFeedbackPayload();
        const response = await agent.addFeedback.post(payload, chatbot_token);
        if (response.status) {
            if (response.data) {
                showSuccess("Feedback submitted successfully!")
                setSelectedIssue(null);
                onCloseOfFeedbackForm();

                const updatedThread = {
                    ...currentActiveChatbotThread,
                    chatbot_thread_data: currentActiveChatbotThread?.chatbot_thread_data?.map((thread: any) => {
                        if(thread?.action_execution_data?.ai_execution_id === payload?.feedback_unique_id){
                            return {
                                ...thread,
                                action_execution_data: {
                                    ...thread?.action_execution_data,
                                    chatbot_message_feedback: response?.data ?? [],
                                }
                            }
                        }
                        return thread;
                    })
                }
                setCurrentActiveChatbotThread(updatedThread);
                // if (chatbotThreadId && feedbackFor.toLowerCase() === "message") {
                //     let temp = { ...currentActiveChatbotThread };
                //     let feedbackData = response.data;

                //     temp = temp.chatbot_thread_data?.map((item: any) =>
                //         item.chatbot_thread_id === chatbotThreadId ? { ...item, chatbot_message_feedback: feedbackData } : item
                //     )

                //     setCurrentActiveChatbotThread((prev: any) => {
                //         return { ...prev, chatbot_thread_data: temp }
                //     })
                // }
            }
        } else {
            handleError(response);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))
    }

    return (
        <>
            {/* <Grid container>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item xs={12} className={styles.chat_header_item} sx={{ padding: '20px' }}>
                            <Grid container sx={{ alignItems: 'center' }}>
                                <Grid item xs={11}>
                                    <Typography sx={{ color: '15px', fontWeight: '500' }}>
                                        Provide your feedback
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: 'end', display: 'flex', justifyContent: 'end' }}>
                                    <Close
                                        sx={{ cursor: 'pointer', display: 'flex', justifyContent: "end", fontSize: '20px' }}
                                        onClick={() => {
                                            setFlagFeedbackDialog(false)
                                            setFlagTellusMoreOptionBtn(null)
                                            setFeedbackData({
                                                ...feedbackData,
                                                flagFeedbackDialog: false
                                            })
                                            setFlagOtherSelected(false)
                                            setFeedbackMessageNewFormat(initialNewFormatMessage)
                                        }}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>

                        <Divider style={{ width: '100%' }} />

                        <Grid item xs={12} sx={{ padding: '10px 20px 15px' }}>
                            <Grid container direction={"column"} >
                                <Grid item><InputLabel sx={{ fontSize: '14px', fontWeight: '500', color: '#000', marginBottom: "2px" }}>Category of Issue</InputLabel></Grid>
                                <Grid item>
                                    <Grid container>
                                        {feedbackOptions.map((ele, index) => {
                                            return (
                                                <>
                                                    <Grid item className={styles.chat_feedback_btn_item}>
                                                        <Button
                                                            variant='outlined'
                                                            size='medium'
                                                            className={`${styles.chatbot_submit_btn_hover} ${ele === feedbackMessageNewFormat.chatbot_message_feedback ? styles.chat_feedback_selected_btn : styles.chat_feedback_btn}`}
                                                            onClick={(e) => {
                                                                if (ele === "Other") {
                                                                    setFlagOtherSelected(true)
                                                                    handleTellUsMoreIssue(ele, "chatbot_message_feedback")
                                                                } else {
                                                                    setFlagOtherSelected(false)
                                                                    handleTellUsMoreIssue(ele, "chatbot_message_feedback")
                                                                }
                                                            }}
                                                        >
                                                            {ele}
                                                        </Button>
                                                    </Grid>
                                                </>
                                            )
                                        })}
                                    </Grid>
                                </Grid>

                            </Grid>

                            <Grid item xs={12} sx={{ margin: '5px 0px 10px ' }}>
                                <Grid container direction={"column"} gap={"7px"}>
                                    <Grid item>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Describe the Issue {flagOtherSelected && <Required />}</span>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            className={styles.chat_feedback_textfield}
                                            placeholder='Briefly describe the issue you encountered'
                                            autoComplete='off'
                                            multiline
                                            value={feedbackMessageNewFormat.chatbot_message_feedback_issue_description}
                                            onChange={(e) => { handleTellUsMoreIssue(e.target.value, "chatbot_message_feedback_issue_description") }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ margin: '10px 0px ' }}>
                                <Grid container direction={"column"} gap={"7px"}>
                                    <Grid item>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Proposed Correct Response</span>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            className={styles.chat_feedback_textfield}
                                            placeholder='Suggest what the correct response should have been'
                                            autoComplete='off'
                                            multiline
                                            value={feedbackMessageNewFormat.chatbot_message_feedback_correct_response}
                                            onChange={(e) => { handleTellUsMoreIssue(e.target.value, "chatbot_message_feedback_correct_response") }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ margin: '10px 0px ' }}>
                                <Grid container direction={"column"} gap={"7px"}>
                                    <Grid item>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Which file would have provided the correct response</span>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            className={styles.chat_feedback_textfield}
                                            placeholder='Write file description here...'
                                            autoComplete='off'
                                            multiline
                                            value={feedbackMessageNewFormat.chatbot_message_feedback_correct_response_file}
                                            onChange={(e) => { handleTellUsMoreIssue(e.target.value, "chatbot_message_feedback_correct_response_file") }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'start' }}>
                                <Button
                                    variant='contained'
                                    size='small'
                                    className={styles.feedback_submit_btn}
                                    sx={{ opacity: (feedbackMessageNewFormat.chatbot_message_feedback.trim() === "") || (flagOtherSelected && feedbackMessageNewFormat.chatbot_message_feedback_issue_description.trim() === "") ? "0.5" : "1" }}
                                    disabled={(feedbackMessageNewFormat.chatbot_message_feedback.trim() === "") || (flagOtherSelected && feedbackMessageNewFormat.chatbot_message_feedback_issue_description.trim() === "") ? true : false}
                                    onClick={() => { handleSubmitFeedback(chatbot_thread_id, feedbackMessageNewFormat) }}
                                >
                                    Submit
                                </Button>
                            </Grid>
                        </Grid>


                    </Grid>
                </Grid>
            </Grid> */}

            {feedbackOptionsData != null &&
                <Grid container>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12} className={styles.provideFeedbackTitleGrid}>
                                <Grid container className={globalStyles.alignItemCenter}>

                                    <Grid item xs={11}>
                                        <Typography className={`${globalStyles.primaryFontSize} ${globalStyles.secondaryFontWeight}`}>
                                            Provide your feedback
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={1} className={`${globalStyles.textItemsEnd} ${globalStyles.displayFlexStyle} ${globalStyles.contentEnd}`}>
                                        <Close
                                            className={styles.closeFeedbackPopupIcon}
                                            onClick={() => { onCloseOfFeedbackForm() }}
                                        // onClick={() => {
                                        //     setFlagFeedbackDialog(false)
                                        //     setFlagTellusMoreOptionBtn(null)
                                        //     setFeedbackData({
                                        //         ...feedbackData,
                                        //         flagFeedbackDialog: false
                                        //     })
                                        //     setFlagOtherSelected(false)
                                        //     setFeedbackMessageNewFormat(initialNewFormatMessage)
                                        // }}
                                        />
                                    </Grid>

                                </Grid>
                            </Grid>

                            <Divider className={globalStyles.fullwidth} />

                            <Grid item xs={12} className={styles.provideFeedbackPopupBody}>
                                <Grid container direction={"column"} >
                                    {feedbackOptionsData != null &&
                                        <Grid item>
                                            <InputLabel className={styles.categoryOfIssueText}>Category of Issue <Required /> </InputLabel>
                                        </Grid>
                                    }
                                    <Grid item>
                                        <Grid container>
                                            {/* {feedbackOptions.map((ele, index) => {
                                            return (
                                                <>
                                                    <Grid item className={chat_feedback_btn_item}>
                                                        <Button
                                                            variant='outlined'
                                                            size='medium'
                                                            className={`${chatbot_submit_btn_hover} ${ele === feedbackMessageNewFormat.chatbot_message_feedback ? chat_feedback_selected_btn : chat_feedback_btn}`}
                                                            onClick={(e) => {
                                                                if (ele === "Other") {
                                                                    setFlagOtherSelected(true)
                                                                    handleTellUsMoreIssue(ele, "chatbot_message_feedback")
                                                                } else {
                                                                    setFlagOtherSelected(false)
                                                                    handleTellUsMoreIssue(ele, "chatbot_message_feedback")
                                                                }
                                                            }}
                                                        >
                                                            {ele}
                                                        </Button>
                                                    </Grid>
                                                </>
                                            )
                                        })} */}
                                            {feedbackOptionsData?.map((item, ind) => {
                                                return (
                                                    <React.Fragment key={ind}>
                                                        <Grid item className={`${styles.chat_feedback_btn_item}`}>
                                                            <Button
                                                                variant='outlined'
                                                                size='medium'
                                                                className={`${styles.chatbot_submit_btn_hover} ${item.feedback_option_name === selectedIssue?.feedback_option_name ? `${styles.chat_feedback_selected_btn}` : `${styles.chat_feedback_btn}`}`}
                                                                onClick={() => {
                                                                    const updatedData = {
                                                                        ...item,
                                                                        feedback_option_field: item.feedback_option_field.map((field) => ({
                                                                            ...field,
                                                                            feedback_value: "",
                                                                        }))
                                                                    }
                                                                    setSelectedIssue(updatedData);
                                                                }}
                                                            >
                                                                {item.feedback_option_name}
                                                            </Button>
                                                        </Grid>
                                                    </React.Fragment>
                                                )
                                            })}
                                        </Grid>
                                    </Grid>

                                </Grid>

                                {/* <Grid item xs={12} sx={{ margin: '5px 0px 10px ' }}>
                                <Grid container direction={"column"} gap={"7px"}>
                                    <Grid item>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Describe the Issue
                                             </span>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            className="chat_feedback_textfield"
                                            placeholder='Briefly describe the issue you encountered'
                                            autoComplete='off'
                                            multiline
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ margin: '10px 0px ' }}>
                                <Grid container direction={"column"} gap={"7px"}>
                                    <Grid item>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Proposed Correct Response</span>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            className="chat_feedback_textfield"
                                            placeholder='Suggest what the correct response should have been'
                                            autoComplete='off'
                                            multiline
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ margin: '10px 0px ' }}>
                                <Grid container direction={"column"} gap={"7px"}>
                                    <Grid item>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#000' }}>Which file would have provided the correct response</span>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            size='small'
                                            fullWidth
                                            className="chat_feedback_textfield"
                                            placeholder='Write file description here...'
                                            autoComplete='off'
                                            multiline
                                        />
                                    </Grid>
                                </Grid>
                            </Grid> */}

                                {selectedIssue && selectedIssue?.feedback_option_field && selectedIssue?.feedback_option_field?.map((item, ind) => {
                                    return (
                                        <React.Fragment key={ind}>
                                            <Grid item xs={12} className={styles.feedbackOptionFieldGrid}>
                                                <Grid container direction='column' gap='7px'>
                                                    <Grid item>
                                                        <span className={`${globalStyles.secondaryFontSize} ${globalStyles.secondaryFontWeight} ${globalStyles.blackColor}`}>{item.feedback_option_field_value}
                                                            {item.flag_required &&
                                                                <Required />
                                                            }
                                                        </span>
                                                    </Grid>
                                                    <Grid item>
                                                        {item.field_input_type_name === "Long Text" ?
                                                            <>
                                                                <textarea
                                                                    rows={3}
                                                                    placeholder={item.feedback_option_field_placeholder}
                                                                    className={`${styles.longTextFeedbackInputField} ${styles.textAreaFeedbackOptionField}`}
                                                                    value={item.feedback_value}
                                                                    onChange={(e) => {
                                                                        handleOnChangeOfFeedbackOptions(e.target.value, item.feedback_option_field_id)
                                                                    }}
                                                                />
                                                            </>
                                                            :
                                                            <TextField
                                                                id="outlined-basic"
                                                                variant="outlined"
                                                                size='small'
                                                                fullWidth
                                                                className={`${styles.chat_feedback_textfield}`}
                                                                placeholder={item.feedback_option_field_placeholder}
                                                                // required={item.flag_required}
                                                                autoComplete='off'
                                                                multiline
                                                                value={item.feedback_value}
                                                                onChange={(e) => { handleOnChangeOfFeedbackOptions(e.target.value, item.feedback_option_field_id) }}
                                                            />
                                                        }
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </React.Fragment>
                                    );
                                })}

                                {/* <Grid item xs={6} sx={{textAlign: "start"}}>
                                
                            </Grid> */}

                                {feedbackOptionsData != null &&
                                    <Grid item xs={12}>
                                        <Box className={styles.provideFeedbackButtonBox}>
                                            <Button
                                                variant='outlined'
                                                size='small'
                                                className={`${styles.feedback_reset_btn}`}
                                                onClick={() => setSelectedIssue(null)}
                                                disabled={selectedIssue === null}
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                variant='contained'
                                                size='small'
                                                className={`${styles.feedback_submit_btn}`}
                                                disabled={checkIsSubmitBtnIsDisabled(selectedIssue)}
                                                onClick={handleSubmitClick}
                                            // sx={{ opacity: (feedbackMessageNewFormat.chatbot_message_feedback.trim() === "") || (flagOtherSelected && feedbackMessageNewFormat.chatbot_message_feedback_issue_description.trim() === "") ? "0.5" : "1" }}
                                            // disabled={(feedbackMessageNewFormat.chatbot_message_feedback.trim() === "") || (flagOtherSelected && feedbackMessageNewFormat.chatbot_message_feedback_issue_description.trim() === "") ? true : false}
                                            // onClick={() => { handleSubmitFeedback(chatbot_thread_id, feedbackMessageNewFormat) }}
                                            >
                                                Submit
                                            </Button>
                                        </Box>
                                    </Grid>
                                }
                            </Grid>


                        </Grid>
                    </Grid>
                </Grid>
            }
        </>
    )
}

export default FeedbackDialog